import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { getHouseRulesTrackingFields, STRUCTURED_HOUSE_RULES_FEATURE } from '@/lib/houseRules/structuredPolicies.js';
import { getPropertyChatEventParams } from '@/lib/propertyChatTracking';
import { getCommonTracking } from '@/tracking/common';
import { TRACKING_EMPTY } from '@/tracking/variables';

const ACCOUNT_NAV_GTM_LABELS = {
  t_HOME: 'home',
  t_EDITDETAILS: 'edit details',
  t_WALLET: 'wallet',
  t_MYTRIPS: 'my trips',
};

export default function Tracker(trackers, nuxtApp) {
  const {
    getGtmOptions,
    getSegmentOptions,
  } = getCommonTracking(nuxtApp);

  const trackMyAccountPageView = (gtmPageName, canonicalUrl) => {
    const pageView = {
      ...getGtmOptions,
      gtmPageName,
      gtmClassification: 'MyAccount',
      gtmQuancastPage: 'My+Account',
    };

    if (canonicalUrl) {
      pageView.gtmCanonicalUrl = canonicalUrl;
    }

    trackers?.gtm?.onPageView(pageView);
  };

  const routeStore = useRouteStore();

  const currentPageKey = routeStore.getPageKey;
  let pageEventContext = {};

  const sessionStore = useSessionStore();
  const testingStore = useTestingStore();

  const webNewAutoCompleteService = testingStore.hasEnabledFeature('web_newAutoCompleteService');
  const webAutoCompleteTracking = testingStore.hasEnabledFeature('web_autocomplete_tracking');
  const geoSpatialSearchEnabled = testingStore.hasEnabledFeature('web_geoSpatialSearch');
  const isStructuredHouseRulesEnabled = testingStore.hasEnabledFeature(STRUCTURED_HOUSE_RULES_FEATURE);

  return {
    setPageEventContext: (context = {}) => {
      pageEventContext = context;
    },

    onTrackEvent: (extraSegmentVariables) => {
      const segmentPageData = SEGMENT_PAGE_DATA[currentPageKey] || SEGMENT_PAGE_DATA.other;

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: segmentPageData.EVENT,
        ...extraSegmentVariables,
        ...pageEventContext,
      });
    },

    onLogoutClick: () => {
      const segmentVariables = {
        event_name: 'Login or Signup Event',
        action: 'Logout Successful',
      };
      trackers?.segment.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onLogoutClickGtm: () => {
      trackers?.gtm.onEvent('gtmAccountLogOut');
    },

    onLoginClick: () => {
      const segmentVariables = {
        event_name: 'Login or Signup Event',
        action: 'Login Clicked',
        page_type: 'login',
      };
      trackers?.segment.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onLoginSuccessful: (platform, profilePic, email) => {
      const isAvatarUserAdded = (str) => {
        if (str == null || str === '') {
          return false;
        }
        const google = /com\/a-\/$/.test(str);
        const facebook = /profilepic/.test(str);
        const apple = !/wp\.com\/cdn\.auth0\.com\/avatars/.test(str);
        return google || facebook || apple;
      };

      const segmentVariables = {
        event_name: 'Login or Signup Event',
        action: 'Login Successful',
        page_type: 'login',
        platform,
        profile_picture: !!profilePic,
        email,
      };

      const dataLayerVariables = {
        parameter1: platform,
        parameter2: isAvatarUserAdded(profilePic) || false,
      };

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });

      trackers?.gtm.onEvent('gtmLoginSuccessful', dataLayerVariables);
    },

    onSignupSuccessful: (email) => {
      const segmentVariables = {
        event_name: 'Login or Signup Event',
        action: 'Sign Up Successful',
        email,
        page_type: 'login',
        optin_status: sessionStore.getNewsletterOptin || false,
      };

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });

      trackers?.gtm.onEvent('gtmAccountCreated');
    },

    onSearchSubmitted: (trackingData) => {
      const segmentVariables = {
        event_name: 'Search Event',
        action: 'Search Submitted',
        page_type: sessionStore.getEntryPointForTracking,
        destination_district: trackingData?.destination_district || TRACKING_EMPTY,
        destination_area: trackingData?.destination_area || TRACKING_EMPTY,
        destination_region: trackingData?.destination_region || TRACKING_EMPTY,
        destination_city: trackingData?.destination_city || TRACKING_EMPTY,
        destination_city_id: trackingData?.destination_city_id || TRACKING_EMPTY,
        destination_country: trackingData?.destination_country || TRACKING_EMPTY,
        destination_continent: trackingData?.destination_continent || TRACKING_EMPTY,
        arrival_date: trackingData?.arrival_date || TRACKING_EMPTY,
        departure_date: trackingData?.departure_date || TRACKING_EMPTY,
        number_guests: trackingData?.number_guests || TRACKING_EMPTY,
        number_nights: trackingData?.number_nights || TRACKING_EMPTY,
        lead_time: trackingData?.lead_time || TRACKING_EMPTY,
        search_keywords: trackingData?.search_keywords || TRACKING_EMPTY,
        time_period_used: trackingData?.time_period_used || TRACKING_EMPTY,
        group_type: trackingData?.group_type || TRACKING_EMPTY,
        group_age_range: trackingData?.group_age_range || TRACKING_EMPTY,
        property_type: trackingData?.property_type || TRACKING_EMPTY,
        property_name: trackingData?.property_name || TRACKING_EMPTY,
        product_id: trackingData?.product_id?.toString() || TRACKING_EMPTY,
        sku: trackingData?.sku?.toString() || TRACKING_EMPTY,
        auto_sort_order_applied: trackingData?.auto_sort_order_applied || TRACKING_EMPTY,
        page_sort_order_applied: trackingData?.page_sort_order_applied || TRACKING_EMPTY,
        listing_type: trackingData?.listing_type || TRACKING_EMPTY,
        geospatial_cohort: trackingData?.geospatial_cohort || TRACKING_EMPTY,
        search_submitted_label: trackingData?.search_label || TRACKING_EMPTY,
        search_submitted_type: trackingData?.search_submitted_type || TRACKING_EMPTY,
      };

      trackers?.gtm.onEvent('gtmSearchSubmitted');

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onSearchActivated: (field) => {
      const searchStore = useSearchStore();
      const segmentVariables = {
        event_name: 'Search Event',
        action: 'Search Activated',
        activated_field: field,
        page_type: sessionStore.getEntryPointForTracking,
        search_test_id_1: webNewAutoCompleteService ? 'variation' : 'control',
        geospatial_cohort: geoSpatialSearchEnabled ? 'variation' : 'control',
        search_activated_label: `${searchStore?.getSearchLocation?.label}, ${searchStore?.getSearchLocation?.labelSmall}`,
      };

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onAutoCompleteSearchCall: (term, results) => {
      if (!webAutoCompleteTracking) {
        return;
      }

      const segmentVariables = {
        event_name: 'Search Event',
        action: 'Search Autocomplete Call',
        autocomplete_term: term,
        autocomplete_search_results: results?.length === 1 && results[0]?.type === 'no-results' ? 0 : results?.length,
      };

      const dataLayerVariables = {
        parameter1: term,
        parameter2: results ? results.length : 0,
      };

      trackers?.gtm.onEvent('GTMAutoCompleteSearchCall', dataLayerVariables);
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onAutoCompleteSearchSelect: (term, item) => {
      if (!webAutoCompleteTracking || !item?.type) {
        return;
      }

      let country = '';
      const cityCountry = item.labelSmall?.split(',');

      switch (item.type) {
        case 'city':
        case 'region':
          item.city = item.label;
          country = item.labelSmall.trimStart();
          break;
        case 'citydistricts':
        case 'property':
          item.city = cityCountry[0]?.trimStart();
          country = cityCountry[1]?.trimStart();
          break;
      }

      const segmentVariables = {
        event_name: 'Search Event',
        action: 'Search Autocomplete Select',
        autocomplete_term: term,
        search_selected_label: item.label,
        search_selected_rank: item.rank,
        search_selected_type: item.type,
        search_selected_city: item.city,
        search_selected_country: country,
      };

      const dataLayerVariables = {
        parameter1: `${term}|${item.label}|${item.rank}`,
        parameter2: `${item.type}|${item?.city}|${country}`,
      };

      trackers?.gtm.onEvent('GTMAutoCompleteSearchSelect', dataLayerVariables);
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onRecentDestinationClicked: () => {
      const segmentVariables = {
        event_name: 'Search Event',
        action: 'Recent Search Clicked',
      };
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onRecentDestinationsShown: () => {
      const segmentVariables = {
        event_name: 'Search Event',
        action: 'Recent Search Shown',
      };
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onNewsletterSubmit: (segmentVariables) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onEasterEggFound: (segmentVariables) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onPageFilterClick: (segmentVariables) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onHostelSignupInteraction: (dataLayerVariables) => {
      trackers?.gtm?.onEvent('gtmHostelSignupClick', dataLayerVariables);
    },

    onBubbleMessageClick: (dataLayerVariables) => {
      trackers?.gtm?.onEvent('gtmHomeEvent', dataLayerVariables);
    },

    onCampaignBannerClick(page) {
      if (page === 'homepage') {
        trackers?.segment?.onEvent({
          event_name: 'Homepage Event',
          action: 'All Deals Clicked',
          page_type: 'Homepage',
          ...getSegmentOptions,
        });
      }
    },

    onUnmissableCitiesInteraction(segmentVariables = null) {
      if (segmentVariables !== null) {
        trackers?.segment?.onEvent({
          ...getSegmentOptions,
          ...segmentVariables,
        });
      }
    },

    onBFTermsConditionsClick(page = '') {
      trackers?.segment?.onEvent({
        event_name: page ? 'Black Friday Page Event' : 'Homepage Event',
        action: 'Terms And Conditions Clicked',
        page_type: page ? 'blackFriday' : 'homepage',
        ...getSegmentOptions,
      });
    },

    onBFAllDealsClick() {
      trackers?.segment?.onEvent({
        event_name: 'Homepage Event',
        action: 'Black Friday All Deals Clicked',
        page_type: 'homepage',
        ...getSegmentOptions,
      });
    },

    onBFLPCityOfferClick(data) {
      trackers?.segment?.onEvent({
        event_name: 'Black Friday Page Event',
        bf_city_clicked: data?.city?.[0]?.cityName,
        bf_continent_clicked: data?.continent,
        action: 'City Section Clicked',
        page_type: 'blackFriday',
        ...getSegmentOptions,
      });
    },

    onHostelCardClick(propertyId) {
      trackers?.gtm?.onEvent('gtmHomeEvent', { parameter1: 'hostel card click' });

      trackers?.segment?.onEvent({
        event_name: 'Homepage Event',
        best_property_id_clicked: propertyId,
        action: 'Best Hostel Panel Clicked',
        page_type: 'homepage',
        ...getSegmentOptions,
      });
    },

    onAppDownloadClick(osType = '', isBlackFriday = false) {
      if (osType) {
        trackers?.segment?.onEvent({
          event_name: isBlackFriday ? 'Black Friday Page Event' : 'Homepage Event',
          os_button_clicked: osType,
          action: 'App Store Clicked',
          page_type: isBlackFriday ? 'blackFriday' : 'homepage',
          ...getSegmentOptions,
        });
      }
    },

    onBlogItemInteraction(segmentVariables = null) {
      if (segmentVariables !== null) {
        trackers?.segment?.onEvent({
          ...getSegmentOptions,
          ...segmentVariables,
        });
      }
    },

    onFeaturedPropertyCardClick: (segmentVariables = null) => {
      if (segmentVariables !== null) {
        trackers?.segment?.onEvent({
          ...getSegmentOptions,
          ...segmentVariables,
        });
      }
    },

    onSustainabilityIconClick: (dataLayerVariables, segmentVariables = null) => {
      trackers?.gtm?.onEvent('gtmSustainableIconClicked', dataLayerVariables);

      if (segmentVariables !== null) {
        trackers?.segment?.onEvent({
          ...getSegmentOptions,
          ...segmentVariables,
        });
      }
    },

    onImageGalleryViewed: (title) => {
      trackers?.segment?.onEvent({
        event_name: SEGMENT_PAGE_DATA.property.EVENT,
        action: 'Gallery Clicked',
        page_type: 'staticMicrosite',
        gallery_panel: title, // header or in-page
        ...getSegmentOptions,
        ...pageEventContext,
      });
    },

    onReportPropertyClick: () => {
      trackers?.segment?.onEvent({
        event_name: SEGMENT_PAGE_DATA.property.EVENT,
        action: 'Report Content Link Clicked',
        page_type: 'staticMicrosite',
        ...getSegmentOptions,
        ...pageEventContext,
      });
    },

    onImageGalleryExit: () => {
      trackers?.gtm?.onEvent('gtmMSGalleryExit');
    },

    onMachineTranslationEvent: (dataLayerVariables) => {
      trackers?.gtm?.onEvent('gtmMicrositeMachineTranslation', dataLayerVariables);
    },

    onBFLPNewsletterSubscription: (email) => {
      trackers?.segment?.onEvent({
        event_name: 'Black Friday Page Event',
        email,
        optin_status: true,
        action: 'Newsletter Signup Clicked',
        page_type: 'blackFriday',
        ...getSegmentOptions,
      });
    },

    onBFLPAddRemainder: () => {
      trackers?.segment?.onEvent({
        event_name: 'Black Friday Page Event',
        action: 'Reminder Added',
        page_type: 'blackFriday',
        ...getSegmentOptions,
      });
    },

    onBFLPMapScroll: () => {
      trackers?.segment?.onEvent({
        event_name: 'Black Friday Page Event',
        action: 'Map Scrolled',
        page_type: 'blackFriday',
        ...getSegmentOptions,
      });
    },

    onBFLPDealClick: (property, index, source) => {
      if (property) {
        trackers?.segment?.onEvent({
          event_name: source === 'blackFriday' ? 'Black Friday Page Event' : 'Homepage Event',
          bf_property_clicked: property?.propertyId,
          bf_offer_displayed: property.tag,
          bf_carousel_position_clicked: index + 1,
          action: 'Popular Hostel Section Clicked',
          page_type: 'blackFriday',
          ...getSegmentOptions,
        });
      }
    },

    onBFLPMapPropertyClick: (property) => {
      if (property) {
        trackers?.segment?.onEvent({
          event_name: 'Black Friday Page Event',
          bf_property_clicked: property?.id,
          bf_city_clicked: property?.tracking?.city,
          bf_continent_clicked: property?.tracking?.continent,
          action: 'Map Section Property Clicked',
          page_type: 'blackFriday',
          ...getSegmentOptions,
        });
      }
    },

    onBFLPWishListClick: () => {
      trackers?.segment?.onEvent({
        event_name: 'Black Friday Page Event',
        action: 'Wishlist Clicked',
        page_type: 'blackFriday',
        ...getSegmentOptions,
      });
    },

    // ### SOCIAL CONSENT MODAL TRACKING EVENTS
    triggerSocialPermissionsEventAction: (extraSegmentVariables) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'Login or Signup Event',
        ...extraSegmentVariables,
      });
    },

    // ### LINKUPS TRACKING EVENTS
    triggerLinkupEventAction: (extraSegmentVariables) => {
      const segmentPageData = SEGMENT_PAGE_DATA[currentPageKey] || SEGMENT_PAGE_DATA.other;

      if (segmentPageData.EVENT) {
        trackers?.segment?.onEvent({
          ...getSegmentOptions,
          event_name: segmentPageData.EVENT,
          ...extraSegmentVariables,
          ...pageEventContext,
        });
      }
    },

    // ### SOCIAL CUES TRACKING EVENTS
    triggerSocialCuesEventAction: (extraSegmentVariables) => {
      const segmentPageData = SEGMENT_PAGE_DATA[currentPageKey] || SEGMENT_PAGE_DATA.other;

      if (segmentPageData.EVENT) {
        trackers?.segment?.onEvent({
          ...getSegmentOptions,
          event_name: segmentPageData.EVENT,
          ...extraSegmentVariables,
          ...pageEventContext,
        });
      }
    },

    // ### CAMPAIGN BANNER TRACKING EVENTS
    triggerCampaignBannerEventAction: (extraSegmentVariables) => {
      const segmentPageData = SEGMENT_PAGE_DATA[currentPageKey] || SEGMENT_PAGE_DATA.other;

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: segmentPageData.EVENT,
        ...extraSegmentVariables,
      });
    },

    // ### TOP PROPERTIES BY FILTER TRACKING EVENTS
    triggerCityTopPropertiesByFiltersAction: (extraSegmentVariables) => {
      const segmentPageData = SEGMENT_PAGE_DATA[currentPageKey] || SEGMENT_PAGE_DATA.other;

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: segmentPageData.EVENT,
        ...extraSegmentVariables,
      });
    },

    // ### SOCIAL HIGHLIGHTS TRACKING EVENTS
    triggerSocialHighlightsEventAction: (extraSegmentVariables) => {
      const segmentPageData = SEGMENT_PAGE_DATA[currentPageKey] || SEGMENT_PAGE_DATA.other;

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: segmentPageData.EVENT,
        ...extraSegmentVariables,
      });
    },

    // ### 3PI EXPERIMENT TRACKING EVENTS (COD-877)
    // event_name/action are set after the spread so callers can pass the page-view variables wholesale.
    onThirdPartyResultsLoaded: (segmentVariables) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
        event_name: 'Third Party Results Loaded',
      });
    },

    onThirdPartyPropertyClicked: (segmentVariables) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
        event_name: 'Destination Landing Page Event',
        action: 'Third Party Property Clicked',
      });
    },

    onThirdPartyError: (errorType = 'API') => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'Third Party Error Event',
        page_type: 'staticFab',
        error_type: errorType,
      });
    },

    // ### MY TRIPS TRACKING EVENTS
    onTripsAllPastViewed: () => {
      trackers?.gtm?.onEvent('gtmAccountPastTrips');

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'All Past Trips Viewed',
      });
    },

    onTripsAllCancelledViewed: () => {
      trackers?.gtm?.onEvent('gtmAccountCancelledTrips');

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'All Cancelled Trips Viewed',
      });
    },

    onTripsPastTripViewed: (propertyId, isThirdPartyBooking = false, extraTracking = {}) => {
      const {
        isOnTrip = false,
        propertyChatFlag,
        houseRules,
      } = extraTracking || {};
      const trackingData = {
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Past Trip Viewed',
        property_id: propertyId,
        ...getHouseRulesTrackingFields(houseRules, isStructuredHouseRulesEnabled),
      };

      if (propertyChatFlag !== undefined) {
        Object.assign(
          trackingData,
          getPropertyChatEventParams(isOnTrip, { propertyChatFlag }),
        );
      }

      if (isThirdPartyBooking) {
        trackingData['3pi_booking'] = true;
      }

      trackers?.segment?.onEvent(trackingData);
    },

    onTripsCancelledTripViewed: (propertyId, isThirdPartyBooking = false, extraTracking = {}) => {
      const trackingData = {
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Cancelled Trip Viewed',
        property_id: propertyId,
        ...getHouseRulesTrackingFields(extraTracking?.houseRules, isStructuredHouseRulesEnabled),
      };

      if (isThirdPartyBooking) {
        trackingData['3pi_booking'] = true;
      }

      trackers?.segment?.onEvent(trackingData);
    },

    onTripsCancelledPropertyViewed: (propertyId, bookingId, customerId) => {
      if (bookingId != null && bookingId !== '') {
        trackers?.gtm?.onEvent('gtmCancelledPropertyViewPropertyClick', {
          parameter1: bookingId,
          parameter2: customerId,
        });
      }

      if (propertyId == null || propertyId === '') {
        return;
      }

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Cancelled Property Viewed',
        property_id: propertyId,
      });
    },

    onTripsPastPropertyViewed: (propertyId, bookingId, customerId) => {
      if (bookingId != null && bookingId !== '') {
        trackers?.gtm?.onEvent('gtmPastPropertyViewPropertyClick', {
          parameter1: bookingId,
          parameter2: customerId,
        });
      }

      if (propertyId == null || propertyId === '') {
        return;
      }

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Past Property Viewed',
        property_id: propertyId,
      });
    },

    onTripsUpcomingPropertyViewPropertyClick: (bookingId, customerId) => {
      if (bookingId == null || bookingId === '') {
        return;
      }

      trackers?.gtm?.onEvent('gtmUpcomingPropertyViewPropertyClick', {
        parameter1: bookingId,
        parameter2: customerId,
      });
    },

    onTripsCancelBookingMenuClicked: () => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Cancel Booking Menu Clicked',
      });
    },

    onTripsCancelBookingButtonClicked: (cancelledReasonGiven) => {
      if (cancelledReasonGiven == null || cancelledReasonGiven === '') {
        return;
      }

      trackers?.gtm?.onEvent('gtmMyAccountBookingCancelReason', { parameter1: cancelledReasonGiven });

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Cancel Booking Button Clicked',
        cancelled_reason_given: cancelledReasonGiven,
      });
    },

    onTripsBookingCancelled: (bookingId, propertyId) => {
      if (bookingId == null || bookingId === '' || propertyId == null || propertyId === '') {
        return;
      }

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Booking Cancelled',
        cancelled_booking_id: bookingId,
        cancelled_property_id: propertyId,
      });
    },

    onTripsTravellersInvited: () => {
      trackers?.gtm?.onEvent('gtmInviteTravellersClick');

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Travellers Invited',
      });
    },

    onTripsMapButtonClicked: () => {
      trackers?.gtm?.onEvent('gtmMapButtonClicks');
    },

    onAccountTripPageView: (gtmPageName, canonicalUrl) => {
      if (gtmPageName == null || gtmPageName === '') {
        return;
      }

      trackMyAccountPageView(gtmPageName, canonicalUrl);
    },

    onTripsUpcomingTripViewed: (
      propertyId,
      appDownloadBannerDisplayed,
      isThirdPartyBooking = false,
      propertyChatOptions = {},
    ) => {
      const trackingData = {
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Upcoming Trip Viewed',
        property_id: propertyId,
        app_download_banner_displayed: appDownloadBannerDisplayed,
        ...getPropertyChatEventParams(
          appDownloadBannerDisplayed === 'on trip',
          propertyChatOptions || {},
        ),
        ...getHouseRulesTrackingFields(propertyChatOptions?.houseRules, isStructuredHouseRulesEnabled),
      };

      if (isThirdPartyBooking) {
        trackingData['3pi_booking'] = true;
      }

      trackers?.segment?.onEvent(trackingData);
    },

    onTripsPropertyChatButtonClicked: (isOnTrip) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Property Chat Button Clicked',
        ...getPropertyChatEventParams(isOnTrip),
      });
    },

    onTripsPropertyChatAppDownloadClicked: (isOnTrip) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Property Chat App Download Clicked',
        ...getPropertyChatEventParams(isOnTrip),
      });
    },

    onTripsRebookClicked: (tripType) => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Rebook Clicked',
        rebook_clicked: tripType,
      });
    },

    onTripsAddReviewClicked: (customerId, propertyId) => {
      if (customerId != null && customerId !== '' && propertyId != null && propertyId !== '') {
        trackers?.gtm?.onEvent('gtmPropertyReviewInitiate', {
          parameter1: customerId,
          parameter2: propertyId,
        });
      }

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Add Review Clicked',
      });
    },

    onTripsExternalBookingContactSupport: () => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Contact Support',
      });
    },

    onTripsExternalBookingOpenApp: () => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Open The App',
      });
    },

    onPropertyReviewFunnelStepViewed: (stepName) => {
      if (stepName == null || stepName === '') {
        return;
      }

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Property Review Funnel Step Viewed',
        step_name: stepName,
      });
    },

    onPropertyReviewFunnelStepCompleted: (stepLabel, propertyId) => {
      if (stepLabel == null || stepLabel === '' || propertyId == null) {
        return;
      }

      trackers?.gtm?.onEvent('gtmPropertyReviewFunnel', {
        parameter1: stepLabel,
        parameter2: propertyId,
      });
    },

    onPropertyReviewAbandoned: (stepTechnicalName, tripBookingId) => {
      if (stepTechnicalName == null || stepTechnicalName === '' || tripBookingId == null || tripBookingId === '') {
        return;
      }

      trackers?.gtm?.onEvent('gtmPropertyReviewAbandoned', {
        parameter1: stepTechnicalName,
        parameter2: tripBookingId,
      });
    },

    onSubmitReviewClicked: () => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Submit Review Clicked',
      });
    },

    onReviewSubmitted: (propertyId, bookingId, reviewRating) => {
      if (propertyId == null || bookingId == null || reviewRating == null) {
        return;
      }

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Review Submitted',
        reviewed_property_id: propertyId,
        order_id: bookingId,
        review_rating: reviewRating,
      });
    },

    reset: () => {
      trackers?.segment?.reset();
    },

    onIdentify: (profileInfo) => {
      trackers?.segment?.onIdentify(profileInfo);
    },

    // ### WALLET TRACKING EVENTS
    onWalletNewCardAdded: (customerId) => {
      trackers?.gtm?.onFormattedEvent('gtmWalletNewCardAdded', customerId);

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'New Card Added',
      });
    },

    onWalletCardDeleted: (customerId) => {
      trackers?.gtm?.onFormattedEvent('gtmWalletCardDeleted', customerId);

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Card Deleted',
      });
    },

    // ### ACCOUNT TRACKING EVENTS

    onAccountHomePageView: (isOnTrip, hasUpcomingBookings, canonicalUrl) => {
      let bannerType = 'no trip';

      if (isOnTrip && hasUpcomingBookings) {
        bannerType = 'on trip';
      } else if (!isOnTrip && hasUpcomingBookings) {
        bannerType = 'upcoming trip';
      }

      const segmentVariables = {
        event_name: 'My Account Home',
        action: 'page view',
        page_type: 'account',
        app_download_banner_displayed: bannerType,
      };

      trackMyAccountPageView('myAccountHome', canonicalUrl);
      trackers?.segment?.onPageView({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onAccountNavigation: (labelKey) => {
      const label = ACCOUNT_NAV_GTM_LABELS[labelKey];
      if (!label) {
        return;
      }

      trackers?.gtm?.onEvent('gtmAccountHome', { parameter1: label });
    },

    onAccountAppDownloadBannerClicked: (clickedBanner, clickedPage) => {
      const segmentVariables = {
        event_name: 'My Bookings Event',
        action: 'App Download Banner Clicked',
        app_download_banner_clicked: clickedBanner,
        app_download_banner_clicked_page: clickedPage,
      };

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        ...segmentVariables,
      });
    },

    onAccountAiraloPartnerPromoClicked: () => {
      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My_Bookings_Event',
        action: 'Partner Promo Clicked',
        partner_promo: 'Airalo',
      });
    },

    onAccountDetailsPageView: (canonicalUrl) => {
      trackMyAccountPageView('myAccountEditDetails', canonicalUrl);

      trackers?.segment?.onPageView({
        ...getSegmentOptions,
        event_name: 'My Account Edit Details',
        action: 'page view',
        page_type: 'accountDetails',
      });
    },

    onAccountDetailsUpdatePasswordClick: () => {
      trackers?.gtm?.onEvent('gtmEditDetailsUpdatePassword');

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Update Password Clicked',
      });
    },

    onAccountDetailsEditDetailsSaved: (socialEnabled, customerId) => {
      trackers?.gtm?.onEvent('gtmEditDetailsSocialToggle', {
        parameter1: socialEnabled ? 'checked' : 'unchecked',
        parameter2: customerId,
      });
      trackers?.gtm?.onEvent('gtmEditDetailsChangesSaved');

      trackers?.segment?.onEvent({
        ...getSegmentOptions,
        event_name: 'My Bookings Event',
        action: 'Changes Saved',
        social_button_toggle: socialEnabled,
      });
    },
  };
}
